import { startOfMinute } from 'date-fns'
import { formatMinutes } from './formatMinutes'
import type { TimeMeasurementFormat } from './timeMeasurementQuestion'
import type { AnswerBase } from '../answer'
import type { DateTimeQuestionNode } from '../dateTimeQuestionNode/dateTimeQuestionNode'

export type TimeMeasurementAnswer = AnswerBase<'timeMeasurement'> & {
  /** 経過時間(分)。計測できていない状態はこの型では表さない */
  value: number
  isInvalid: boolean
}

/** 計測できる経過時間の上限。フォーマットが日時の場合のみ適用される */
const DATETIME_MAX_MINUTES = 48 * 60
const MINUTES_PER_DAY = 24 * 60

/**
 * 開始・終了の日時回答から経過時間(分)を計算する。秒以下は切り捨てる。
 * 計測できない場合(未回答・開始が終了より後・上限超過)は undefined を返す。
 *
 * @param format 計測に使う日時質問のフォーマット
 * @param startNode 開始時間の日時質問ノード
 * @param endNode 終了時間の日時質問ノード
 */
const calc = (
  format: TimeMeasurementFormat,
  startNode: DateTimeQuestionNode | undefined,
  endNode: DateTimeQuestionNode | undefined,
): number | undefined => {
  const start = startNode?.answer?.value
  const end = endNode?.answer?.value
  if (start === undefined || end === undefined) {
    return undefined
  }

  const startAt = startOfMinute(start)
  const endAt = startOfMinute(end)

  switch (format) {
    case 'dateTime': {
      const minutes = (endAt.getTime() - startAt.getTime()) / 1000 / 60
      if (minutes < 0 || minutes > DATETIME_MAX_MINUTES) {
        return undefined
      }
      return minutes
    }
    case 'time': {
      // 時刻フォーマットでは日付を持たないため、終了が開始より前の場合は日を跨いだものとして扱う
      const minutes = minutesOfDay(endAt) - minutesOfDay(startAt)
      return minutes < 0 ? MINUTES_PER_DAY + minutes : minutes
    }
  }
}

const minutesOfDay = (date: Date): number =>
  date.getHours() * 60 + date.getMinutes()

/**
 * 表示用の文字列に変換する
 */
const toDisplayStringAnswer = (
  answer: TimeMeasurementAnswer | undefined,
): string => {
  if (answer === undefined) {
    return '未計測'
  }
  return formatMinutes(answer.value)
}

export const TimeMeasurementAnswer = {
  calc,
  toDisplayStringAnswer,
}
